import React from "react";
import styled from "styled-components";
import { useTranslate } from '@tolgee/react';
import { useAuthService } from "../../Context/AuthContext";
import { displayAvatar } from "../../Helpers/chatHelper/AvatarHelper";
import { displayName } from "../../Helpers/chatHelper/NameHelper";
import { ReactComponent as VideoCall } from "../../Assets/iconsv2/generic/Video.svg";
import { ReactComponent as CallIcon } from "../../Assets/iconsv2/generic/Phone.svg";

import { ReactComponent as MissedIcon } from "../../Assets/iconsv2/call/missed.svg";
import moment from "moment";
import IconButton from "../GenericComponents/IconButton";
import { useCallsService } from "../../Context/CallsContext";

const CallListElement = ({ handleSelectCall, data, selected, displayMenu }) => {
  const { user } = useAuthService();
  const { activeCallRef } = useCallsService();
  const { t } = useTranslate();
  const displayNewOrTimeAgo = (data) => {
    const created_at = moment(data.created_at);
    const nowDate = moment();
    let daysBefore = created_at.diff(nowDate, "days");

    if (daysBefore === 0) {
      return created_at.format("HH:mm");
    }

    if (0 > daysBefore > -7) {
      return created_at.format("dddd");
    }

    if (-7 > daysBefore) {
      return created_at.format("DD-MM-YYYY");
    }
  };

  const verifyCallStatus = (call) => {
    if (!call) {
      return;
    }

    let status = "ongoing";
    if (call.close_date === null && call.missed !== true) {
      status = "ongoing";
    } else if (call.created_by.id === user.id) {
      status = "outgoing";
    } else if (call.missed) {
      status = "missed";
    } else {
      status = "incoming";
    }
    return status;
  };

  const isVideoCall = (call) => {
    let displayVideoText = <div></div>;
    if (call.video) {
      displayVideoText = (
        <CallDirection status={verifyCallStatus(data)}>
          <p>({t('general.video')})</p>
        </CallDirection>
      );
    }

    return displayVideoText;
  };
  const callStatusAndDirection = (call) => {
    let displayDirection = <div></div>;
    let status = verifyCallStatus(call);

    switch (status) {
      case "ongoing":
        displayDirection = (
          <CallDirection status={verifyCallStatus(data)}>
            <p>{t('call_status.ongoing')}</p>
          </CallDirection>
        );
        break;
      case "outgoing":
        displayDirection = (
          <CallDirection>
            <p> {t('call_status.outgoing')}</p>
          </CallDirection>
        );
        break;
      case "incoming":
        displayDirection = (
          <CallDirection>
            <p>{t('call_status.incoming')}</p>
          </CallDirection>
        );
        break;
      case "missed":
        displayDirection = (
          <CallDirection status={verifyCallStatus(data)}>
            <MissedIcon />
            <p>{t('call_status.missed')}</p>
          </CallDirection>
        );
        break;

      default:
        break;
    }
    return displayDirection;
  };

  const handleSelectCallNoPropagation = (e, call, video) => {
    e.stopPropagation();
    if (activeCallRef.current) {
      return;
    }
    handleSelectCall(call, video);
  };

  const finalCallDuration = (call) => {
    if (call.missed) return;
    if (call.close_date && call.answer_date) {
      let diff = moment(call.close_date).diff(call.answer_date);
      return moment.utc(diff).format(diff >= 3600000 ? 'HH:mm:ss' : 'mm:ss');
    }
  };

  return (
    <CallListElementContainer
      selected={selected}
      onContextMenu={(e) => displayMenu(e, data)}
      onClick={(e) =>
        verifyCallStatus(data) === "ongoing" &&
        handleSelectCallNoPropagation(e, data, false)
      }
    >
      <UserAvatar style={data.quick_chat ? { border: "none" } : {}}>
        {displayAvatar(data)}
      </UserAvatar>
      <Text>
        <PhoneNumber status={verifyCallStatus(data)}>
          <h1>{displayName(data)}</h1>{" "}
          {verifyCallStatus(data) === "ongoing" && <OngoingCircle />}
          <h3> {moment(data.created_at).format("HH:mm")} </h3>
        </PhoneNumber>
        <Message status={verifyCallStatus(data)}>
          {callStatusAndDirection(data)}
          {isVideoCall(data)}
          <h3>
            {finalCallDuration(data) ? `(${finalCallDuration(data)}) ` : ""}
            {moment(data.created_at).format("DD-MM-YYYY")}
          </h3>
        </Message>
      </Text>

      <Actions>
        <IconButton
          icon={<VideoCall />}
          onClick={(e) => handleSelectCallNoPropagation(e, data, true)}
        />
        <IconButton
          icon={<CallIcon />}
          onClick={(e) => handleSelectCallNoPropagation(e, data, false)}
        />
      </Actions>
    </CallListElementContainer>
  );
};

export default CallListElement;

const Actions = styled.div`
  display: none;
  align-items: center;
  gap: 8px;
  svg {
    color: ${({ theme }) => theme.primaryButton.color};
    width: 20px;
    height: 20px;
  }
`;
const OngoingCircle = styled.div`
  display: flex;
  min-width: 6px;
  min-height: 6px;
  border-radius: 100px;
  background: ${({ theme }) => theme.menu.selectedTextColor};
`;

const CallListElementContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 13px 16px;
  background: ${({ selected, theme }) =>
    selected
      ? theme.call.callElementSelectedBackground
      : theme.call.callElementBackground};
  gap: 16px;
  border: ${(props) => (props.selected ? "1px solid #5852D633" : "")};
  margin: ${(props) => (props.selected ? "-1px" : "")};

  &:hover {
    background: ${({ theme }) => theme.call.callElementSelectedBackground};
  }

  &:hover ${Actions} {
    display: flex;
  }

  &:hover h3 {
    display: none;
  }
`;
const UserAvatar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  background: ${({ selected, theme }) =>
    selected ? theme.sidebar.selectedBtnBackground : "transparent"};
  color: ${({ selected, theme }) =>
    selected ? theme.sidebar.selectedIconColor : theme.sidebar.iconColor};

  min-width: 42px;
  min-height: 42px;
  max-height: 42px;
  max-width: 42px;
  width: 42px;
  height: 42px;

  svg {
    width: 42px;
    height: 42px;
  }

  img {
    border-radius: 100px;
    border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
    object-fit: cover;
    position: relative;
    height: 42px;
    width: 42px;
  }
`;
const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  width: 100%;
`;

const PhoneNumber = styled.div`
  display: flex;

  align-items: center;
  gap: 3px;
  h1 {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.04px;
    vertical-align: middle;
  }

  h2 {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.04px;
    text-align: left;
  }

  h3 {
    margin-left: auto;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    text-align: left;
  }

  ${({ status, theme }) =>
    status === "ongoing" &&
    `
    h1{
     color:${theme.menu.selectedTextColor};
    }
     h3{
        color:${theme.menu.selectedTextColor};
        }
    `}
`;

const Message = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 2px;

  p {
    width: 100%;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: wrap;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    text-align: left;
  }

  h3 {
    margin-left: auto;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    font-size: 10px;
    font-weight: 500;
    line-height: 14px;
    text-align: left;
  }

  ${({ theme, status }) =>
    status === "ongoing" &&
    `
    h3{
     color:${theme.menu.selectedTextColor};
    }
    `}

  ${(theme, status) =>
    status === "missed" &&
    `
    h3{
    color:${theme.menu.deleteColor};
    }
    `}
`;

const CallDirection = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  svg {
    min-height: 14px;
    min-width: 14px;
    max-width: 14px;
    max-height: 14px;
  }
  p {
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    width: 100%;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: wrap;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    text-align: left;
  }

  ${({ status, theme }) =>
    status === "ongoing" &&
    `
   color:${theme.menu.selectedTextColor};
    p{
   color:${theme.menu.selectedTextColor};
    }
    `}
`;
